<template>
  <PreAuthPage>
    <div class="sh-card__head">
      <LogoTile :branding="branding" :alt="tenantName" :size="44" />
      <h1 class="sh-card__title">{{ done ? t("reset.done") : t("reset.title") }}</h1>
      <p class="sh-card__subtitle">{{ done ? t("reset.doneHint") : t("reset.intro") }}</p>
    </div>

    <!-- 🔴 The server's own sentence, unchanged. It gives ONE message for every way a
         token can be no good — unknown, expired, already spent — so passing it through
         leaks nothing, and "at least 12 characters" is the only thing that tells a
         person standing at this form what to do differently. -->
    <p v-if="errorMessage" class="reset__error sh-card__error" role="alert">
      <v-icon size="15">error_outline</v-icon>
      <span>{{ errorMessage }}</span>
    </p>

    <form v-if="!done" class="login-form" @submit.prevent="submit()">
      <label class="login-field">
        <span class="login-field__label">{{ t("reset.newPassword") }}</span>
        <input
          ref="passwordInput"
          v-model="newPassword"
          class="login-field__input"
          type="password"
          name="new-password"
          autocomplete="new-password"
          required
          data-testid="reset-password"
          :disabled="busy"
        />
      </label>

      <label class="login-field">
        <span class="login-field__label">{{ t("reset.confirmPassword") }}</span>
        <input
          v-model="confirmPassword"
          class="login-field__input"
          type="password"
          name="confirm-password"
          autocomplete="new-password"
          required
          data-testid="reset-confirm"
          :disabled="busy"
        />
      </label>

      <v-btn type="submit" color="primary" size="large" block :loading="busy" data-testid="reset-submit">{{ t("reset.submit") }}</v-btn>
    </form>

    <!-- Nothing is signed in by redeeming: the new password is proved at the front
         door like anybody else's, which keeps ONE way into this application. -->
    <v-btn v-else color="primary" variant="flat" size="large" block data-testid="reset-to-sign-in" @click="toSignIn()">{{
      t("reset.toSignIn")
    }}</v-btn>
  </PreAuthPage>
</template>

<script lang="ts">
/**
 * 🔴 **THE FAR END OF A RESET LINK** (D122, PNK-143) — `/r/<token>`.
 *
 * Drawn by `App.vue` straight off the path, above the session and before it is
 * resolved: somebody arriving here is, by construction, somebody who could not get
 * in, and making them wait on a session boot to be told so would be perverse.
 *
 * 🔴 **THE TOKEN IS NEVER PUT BACK INTO THE URL BAR, A LINK, OR A LOG.** It is read
 * once from the path into a prop and spent. On success the address bar is rewritten
 * to `/` with `replaceState`, so the spent token does not sit in history for the next
 * person on a shared machine to walk back into — and `replace` rather than `push` so
 * the back button cannot return to it either.
 */
import { computed, defineComponent, ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { redeemReset } from "@/auth/resetPassword";
import LogoTile from "@/shell/LogoTile.vue";
import PreAuthPage from "@/shell/PreAuthPage.vue";
import { APP_BRANDING, APP_NAME } from "@/app";

export default defineComponent({
  name: "ResetPassword",
  components: { LogoTile, PreAuthPage },
  props: {
    /** The token out of `/r/<token>`. Read by App.vue so this view never parses a URL. */
    token: { type: String, required: true },
  },
  setup(props) {
    const { t } = useI18n();
    const tenantName = APP_NAME;
    const branding = APP_BRANDING;

    const newPassword = ref("");
    const confirmPassword = ref("");
    const busy = ref(false);
    const done = ref(false);
    const errorMessage = ref<string | null>(null);
    const passwordInput = ref<HTMLInputElement | null>(null);

    onMounted(() => passwordInput.value?.focus?.());

    async function submit(): Promise<void> {
      if (busy.value) return;
      errorMessage.value = null;

      // 🔴 Checked HERE and not at the server, because the server is not told the second
      // one: a mismatch is a typing question between the person and their own screen, and
      // sending a password twice only widens what is on the wire.
      if (newPassword.value !== confirmPassword.value) {
        errorMessage.value = t("reset.mismatch");
        return;
      }

      busy.value = true;
      try {
        const outcome = await redeemReset(props.token, newPassword.value);
        if (!outcome.ok) {
          errorMessage.value = outcome.message;
          return;
        }
        done.value = true;
        // The password is set; nothing on this screen should still be holding it.
        newPassword.value = "";
        confirmPassword.value = "";
        // 🔴 The spent token leaves the address bar — see the header.
        window.history.replaceState(null, "", "/");
      } finally {
        busy.value = false;
      }
    }

    /** A full load rather than a route change: the shell boots at "/" with no session and
     *  draws its own sign-in screen, which is exactly the state this person should land in. */
    function toSignIn(): void {
      window.location.assign("/");
    }

    return {
      t,
      tenantName,
      branding,
      newPassword,
      confirmPassword,
      busy,
      done,
      errorMessage,
      passwordInput,
      submit,
      toSignIn,
      hasToken: computed(() => props.token.trim() !== ""),
    };
  },
});
</script>

<style scoped>
/* 🔴 The form carries `login-form`, not a class of its own, and that is load-bearing
   rather than tidiness: `standalone.css` styles the control as `.login-form
   .login-field__input` to beat a 0,2,0 reset (the specificity trap documented there).
   A lone `.reset__form` would have rendered borderless, invisible inputs on a pale card. */
.reset__error {
  margin-bottom: 12px;
}
</style>
